import { X, Save, Plus, Trash2, Zap } from "lucide-react"
import type { LoadBalancerConfig, LoadBalancerURL, VendorIntake } from '@/features/fmo'
import { calculateTotalPercentage, isPercentageValid, autoConfigurePercentages, getAutoConfigurationMessage } from "../utils/validation"

interface ConfigurationModalProps {
  isOpen: boolean
  config: LoadBalancerConfig | null
  tempUrls: LoadBalancerURL[]
  vendorIntakes: VendorIntake[]
  onClose: () => void
  onSave: () => void
  onUrlChange: (urlId: string, field: keyof LoadBalancerURL, value: any) => void
  onAddUrl: () => void
  onRemoveUrl: (urlId: string) => void
  onAutoConfigure: (urls: LoadBalancerURL[]) => void
}

export default function ConfigurationModal({
  isOpen,
  config,
  tempUrls,
  vendorIntakes,
  onClose,
  onSave,
  onUrlChange,
  onAddUrl,
  onRemoveUrl,
  onAutoConfigure
}: ConfigurationModalProps) {
  if (!isOpen || !config) return null

  const totalPercentage = calculateTotalPercentage(tempUrls)
  const isValid = isPercentageValid(tempUrls)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Configure Load Balancer</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-gray-600 mt-2">{config.organizationName}</p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {tempUrls.map((url) => (
              <div key={url.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <select
                    value={url.linkId}
                    onChange={(e) => {
                      const vendorIntake = vendorIntakes.find(vi => vi.id === e.target.value)
                      onUrlChange(url.id, 'linkId', e.target.value)
                      onUrlChange(url.id, 'vendorIntake', vendorIntake)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Vendor</option>
                    {vendorIntakes.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.vendorName} - {vendor.businessType}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={url.percentage}
                    onChange={(e) => onUrlChange(url.id, 'percentage', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <span className="text-sm text-gray-600 w-8">%</span>
                <button
                  onClick={() => onRemoveUrl(url.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
            
            <button
              onClick={onAddUrl}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              <Plus className="w-5 h-5 mx-auto mb-2" />
              Add Vendor
            </button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-blue-900">Total Percentage:</span>
              <span className={`font-bold ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                {totalPercentage}%
              </span>
            </div>
            
            {tempUrls.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-blue-700">Auto-configure percentages:</span>
                <button
                  onClick={() => onAutoConfigure(autoConfigurePercentages(tempUrls))}
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <Zap className="w-4 h-4" />
                  Auto-configure
                </button>
              </div>
            )}
            
            {tempUrls.length > 0 && (
              <p className="text-xs text-blue-600 mb-2">
                {getAutoConfigurationMessage(tempUrls.length)}
              </p>
            )}
            
            {!isValid && (
              <p className="text-sm text-red-600">
                Total percentage must equal exactly 100%
              </p>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!isValid}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  )
}
